/**
 * MessagePanel - Slide-out panel displaying character messages grouped by stage
 */

import { useEffect } from 'react';
import { X, CheckCheck } from 'lucide-react';
import { IoHome, IoJournal, IoCloudOutline, IoMail } from 'react-icons/io5';
import { FaBrain } from 'react-icons/fa';
import { cn } from '@/lib/utils';
import { formatTimeAgo } from '@/lib/timeUtils';
import { PlayerMessage } from '@/hooks/usePlayerMessages';
import { StageId } from '@/game/models/GameState';

interface MessagePanelProps {
  isOpen: boolean;
  onClose: () => void;
  messages: PlayerMessage[];
  unreadCount: number;
  onMarkAllRead: () => void;
  onMarkRead: (id: string) => void;
}

function MessageIcon({ icon }: { icon: string }) {
  switch (icon) {
    case 'mothership':
      return <img src="/icons/MOTHERSHIP.png" alt="Mothership" className="w-7 h-7 object-contain" />;
    case 'doc':
      return <img src="/icons/doc.png" alt="DOC" className="w-7 h-7 object-contain" />;
    case 'gee':
      return <img src="/icons/GEE.png" alt="GEE" className="w-7 h-7 object-contain" />;
    case 'memory':
      return <FaBrain className="size-5" />;
    case 'journal':
      return <IoJournal className="size-5" />;
    case 'conscience':
      return <IoCloudOutline className="size-5" />;
    case 'home':
      return <IoHome className="size-5" />;
    default:
      return <IoMail className="size-5" />;
  }
}

export function MessagePanel({
  isOpen,
  onClose,
  messages,
  unreadCount,
  onMarkAllRead,
  onMarkRead,
}: MessagePanelProps) {
  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Group messages by stage (newest stage first)
  const groupedByStage = messages.reduce<Record<number, PlayerMessage[]>>((acc, msg) => {
    if (!acc[msg.stage]) acc[msg.stage] = [];
    acc[msg.stage].push(msg);
    return acc;
  }, {});

  const stageKeys = Object.keys(groupedByStage)
    .map(Number)
    .sort((a, b) => b - a) as StageId[];

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]"
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className={cn(
          'fixed top-0 right-0 z-40 h-full w-full sm:w-96 bg-white border-l border-[#CED4DE] shadow-2xl',
          'transform transition-transform duration-300 ease-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#12356B] to-[#0d2850] px-5 py-4">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-bold text-white">Transmissions</h2>
            <button
              onClick={onClose}
              className="size-8 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Close panel"
            >
              <X className="size-5" />
            </button>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-200 text-sm">
              {unreadCount > 0 ? `${unreadCount} unread` : 'All read'}
            </span>
            {unreadCount > 0 && (
              <button
                onClick={onMarkAllRead}
                className="flex items-center gap-1 text-sm text-slate-200 hover:text-white transition-colors"
              >
                <CheckCheck className="size-4" />
                Mark all read
              </button>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="overflow-y-auto h-[calc(100%-88px)] scrollbar-thin">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-6">
              <div className="size-14 rounded-full bg-[#E2E5EC] flex items-center justify-center mb-4">
                <IoMail className="size-7 text-[#12356B]" />
              </div>
              <h3 className="font-semibold text-lg mb-1 text-[#12356B]">No Transmissions</h3>
              <p className="text-sm text-slate-600">
                Messages will appear here as the mission progresses.
              </p>
            </div>
          ) : (
            stageKeys.map(stageId => {
              const stageMessages = groupedByStage[stageId];
              const stageTitle = stageMessages[0]?.stageTitle || `Stage ${stageId}`;
              return (
                <div key={stageId}>
                  {/* Stage header */}
                  <div className="sticky top-0 bg-[#E2E5EC]/90 backdrop-blur-sm px-5 py-2 border-b border-[#CED4DE]">
                    <span className="text-xs font-semibold text-[#12356B] uppercase tracking-wider">
                      Stage {stageId}: {stageTitle}
                    </span>
                  </div>

                  {/* Stage messages */}
                  {stageMessages.map(msg => (
                    <div
                      key={msg.id}
                      className={cn(
                        'px-5 py-4 border-b border-[#CED4DE]/60 transition-colors',
                        !msg.isRead && 'bg-[#12356B]/5'
                      )}
                      onClick={() => {
                        if (!msg.isRead) onMarkRead(msg.id);
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="size-10 bg-[#E2E5EC] rounded-full flex items-center justify-center flex-shrink-0 text-[#12356B]">
                          <MessageIcon icon={msg.icon} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-sm text-[#12356B]">{msg.from}</span>
                            {!msg.isRead && (
                              <span className="size-2 rounded-full bg-[#DD6832] flex-shrink-0" />
                            )}
                            <span className="text-xs text-slate-500 ml-auto flex-shrink-0">
                              {formatTimeAgo(msg.timestamp)}
                            </span>
                          </div>
                          <p className="text-sm leading-relaxed text-slate-700">{msg.text}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}
